import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_BANK_TRANSFER_ROUTER } from "../../../services/api-config";

// ===== Enums (khớp BE order service: bank-transfer.entity.ts) =====
export enum BANK_TRANSFER_MATCH_STATUS {
  MATCHED = 1,
  UNMATCHED = 2,
  UNDERPAID = 3,
  NEEDS_REVIEW = 4,
  IGNORED = 5,
}

export const BANK_TRANSFER_MATCH_STATUS_LABEL: Record<number, string> = {
  [BANK_TRANSFER_MATCH_STATUS.MATCHED]: "Đã khớp đơn",
  [BANK_TRANSFER_MATCH_STATUS.UNMATCHED]: "Chưa khớp đơn",
  [BANK_TRANSFER_MATCH_STATUS.UNDERPAID]: "Chuyển thiếu",
  [BANK_TRANSFER_MATCH_STATUS.NEEDS_REVIEW]: "Cần kiểm tra",
  [BANK_TRANSFER_MATCH_STATUS.IGNORED]: "Đã bỏ qua",
};

export const BANK_TRANSFER_MATCH_STATUS_CLASS: Record<number, string> = {
  [BANK_TRANSFER_MATCH_STATUS.MATCHED]: "bg-green-100 text-green-700",
  [BANK_TRANSFER_MATCH_STATUS.UNMATCHED]: "bg-amber-100 text-amber-700",
  [BANK_TRANSFER_MATCH_STATUS.UNDERPAID]: "bg-red-100 text-red-700",
  [BANK_TRANSFER_MATCH_STATUS.NEEDS_REVIEW]: "bg-blue-100 text-blue-700",
  [BANK_TRANSFER_MATCH_STATUS.IGNORED]: "bg-slate-200 text-slate-600",
};

/** Các trạng thái còn CHỜ kế toán xử lý. */
export const BANK_TRANSFER_PENDING_STATUSES = [
  BANK_TRANSFER_MATCH_STATUS.UNMATCHED,
  BANK_TRANSFER_MATCH_STATUS.UNDERPAID,
  BANK_TRANSFER_MATCH_STATUS.NEEDS_REVIEW,
];

/** Phiếu thu tự động của giao dịch đã khớp đơn (khớp BE: BANK_TRANSFER_VOUCHER_STATUS). */
export enum BANK_TRANSFER_VOUCHER_STATUS {
  NONE = 0,
  CREATED = 1,
  FAILED = 2,
}

export const BANK_TRANSFER_SOURCE_LABEL: Record<number, string> = {
  1: "Tức thời",
  2: "Đối soát cuối ngày",
};

export interface BankTransfer {
  id: number;
  created_at: string;
  bank_code: string;
  transaction_code: string | null;
  account_number: string | null;
  amount: number;
  content: string | null;
  remitter_name: string | null;
  remitter_account_number: string | null;
  remitter_bank_name: string | null;
  /** ISO-8601 nguyên văn từ ngân hàng */
  transaction_date: string | null;
  source: number;
  /** 0 = chưa gán được đơn */
  order_id: number;
  order_code: string | null;
  order_total_amount: number | null;
  match_status: number;
  /** 0 = chưa tạo, 1 = đã tạo, 2 = tạo lỗi */
  voucher_status: number;
  note: string | null;
}

export interface BankTransferSummaryItem {
  match_status: number;
  count: number;
  amount: number;
}

export interface BankTransferListResponse {
  list: BankTransfer[];
  summary: BankTransferSummaryItem[];
  total_record: number;
  page: number;
  limit: number;
}

export interface BankTransferFilters {
  match_status?: number;
  key_search?: string;
  /** DD/MM/YYYY */
  from_date?: string;
  /** DD/MM/YYYY */
  to_date?: string;
  page?: number;
  limit?: number;
}

export interface AssignBankTransferPayload {
  order_code: string;
  /** Chấp nhận ghi nhận dù chuyển thiếu — chỉ gửi sau khi người dùng xác nhận */
  allow_underpaid?: boolean;
}

const BankTransferService = {
  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<BankTransferListResponse>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: BankTransferFilters = {}) =>
        request({
          endPoint: API_BANK_TRANSFER_ROUTER.API_LIST,
          data: {
            ...params,
            page: params.page ?? 1,
            limit: params.limit ?? 20,
          },
        }),
      response: baseResponse,
      loading,
    };
  },
  assignOrder: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BankTransfer>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, payload: AssignBankTransferPayload) =>
        request({ endPoint: API_BANK_TRANSFER_ROUTER.API_ASSIGN_ORDER(id), data: payload }),
      response: baseResponse,
      loading,
    };
  },
  retryVoucher: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BankTransfer>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) => request({ endPoint: API_BANK_TRANSFER_ROUTER.API_RETRY_VOUCHER(id) }),
      response: baseResponse,
      loading,
    };
  },
  ignore: () => {
    const { request, baseResponse, loading } = useFetchData.Post<BankTransfer>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, note?: string) =>
        request({ endPoint: API_BANK_TRANSFER_ROUTER.API_IGNORE(id), data: { note: note || undefined } }),
      response: baseResponse,
      loading,
    };
  },
};

export default BankTransferService;
